import { useState, useEffect } from "react";
import { Box, Dialog, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import NfcIcon from "@mui/icons-material/Nfc";
import SimCardIcon from "@mui/icons-material/SimCard";
import SensorsOutlinedIcon from "@mui/icons-material/SensorsOutlined";
import SensorsIcon from "@mui/icons-material/Sensors";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";

// Tag: public/tag-logo.png
const tagLogoUrl = `${process.env.PUBLIC_URL || ""}/tag-logo.png`;

// Tekktelic: use high-res image for sharp display at 100% zoom; 1x fallback for older paths
const tekktelicRibbonUrl = `${process.env.PUBLIC_URL || ""}/tekktelic-ribbon.png`;
const tekktelicRibbon2xUrl = `${process.env.PUBLIC_URL || ""}/tekktelic-ribbon@2x.png`;
let ribbonFailedGlobal = false;
const ribbonFailedListeners = new Set();
function notifyRibbonFailed() {
  ribbonFailedGlobal = true;
  ribbonFailedListeners.forEach((fn) => fn(true));
}
const tekktelicConfig = {
  ribbon: true,
  tekktelicLogo: true,
  logoImg: tekktelicRibbon2xUrl,
  logoImg2x: undefined,
  icon: SensorsIcon,
  colorKey: "tekktelic",
};

// Oyster: public/oyster.png (used for both Oyster and Oyster2)
const oysterLogoUrl = `${process.env.PUBLIC_URL || ""}/oyster.png`;
let oysterFailedGlobal = false;
const oysterFailedListeners = new Set();
function notifyOysterFailed() {
  oysterFailedGlobal = true;
  oysterFailedListeners.forEach((fn) => fn(true));
}
const oysterConfig = { logo: true, oysterLogo: true, logoImg: oysterLogoUrl, icon: SimCardIcon, colorKey: "oyster" };
const oyster2Config = { logo: true, oysterLogo: true, logoImg: oysterLogoUrl, icon: SensorsOutlinedIcon, colorKey: "oyster2" };

const TRACKER_TYPES = {
  Tag: { logo: true, tagLogo: true, logoImg: tagLogoUrl, colorKey: "tag" },
  Tekktelic: tekktelicConfig,
  Tektelic: tekktelicConfig, // API may return "tektelic" (one k) -> normalizes to "Tektelic"
  NFC: { icon: NfcIcon, colorKey: "secondary", nfcPill: true },
  Oyster: oysterConfig,
  Oyster2: oyster2Config,
};

/** Normalize for lookup: "tag" / "TAG" -> "Tag", trim spaces */
function normalizeType(t) {
  if (!t || typeof t !== "string") return t;
  const s = t.trim();
  if (!s) return t;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/**
 * Premium pill for Tracker Type column: Tag, Tekktelic, NFC, Oyster, Oyster2.
 * Theme-aware colors; centered in cell.
 */
export default function TrackerTypePill({ type }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const rawType = type && typeof type === "string" ? type.trim() : type;
  const label = rawType || "—";
  const config =
    TRACKER_TYPES[rawType] ||
    TRACKER_TYPES[normalizeType(rawType)] ||
    { icon: LabelOutlinedIcon, colorKey: "default" };
  const Icon = config.icon;
  const useCustomImage = config.logo === true || config.ribbon === true;
  const customImageSrc = config.logoImg;
  const [ribbonImageError, setRibbonImageError] = useState(() => (config.ribbon && ribbonFailedGlobal) || false);
  const [oysterImageError, setOysterImageError] = useState(() => (config.oysterLogo && oysterFailedGlobal) || false);
  const [fullImageOpen, setFullImageOpen] = useState(false);
  useEffect(() => {
    if (!config.ribbon) return;
    ribbonFailedListeners.add(setRibbonImageError);
    if (ribbonFailedGlobal) setRibbonImageError(true);
    return () => ribbonFailedListeners.delete(setRibbonImageError);
  }, [config.ribbon]);
  useEffect(() => {
    if (!config.oysterLogo) return;
    oysterFailedListeners.add(setOysterImageError);
    if (oysterFailedGlobal) setOysterImageError(true);
    return () => oysterFailedListeners.delete(setOysterImageError);
  }, [config.oysterLogo]);
  const showCustomImage =
    useCustomImage &&
    customImageSrc &&
    !(config.ribbon && ribbonImageError) &&
    !(config.oysterLogo && oysterImageError);
  const colorMap = {
    tag: {
      // Tag: no background, text matches theme
      bg: "transparent",
      color: theme.palette.text.primary,
      border: "none",
    },
    default: {
      bg: isDark ? "hsl(215, 28%, 22%)" : "hsl(214, 42%, 28%)",
      color: "#fff",
      border: isDark ? "1px solid rgba(148, 163, 184, 0.2)" : "1px solid rgba(30, 58, 85, 0.4)",
    },
    info: {
      bg: isDark ? theme.palette.info.dark : theme.palette.info.main,
      color: "#fff",
      border: isDark ? "1px solid rgba(96, 165, 250, 0.25)" : "1px solid rgba(59, 130, 246, 0.4)",
    },
    tekktelic: {
      // Tekktelic: no background, text matches theme
      bg: "transparent",
      color: theme.palette.text.primary,
      border: "none",
    },
    secondary: {
      // NFC: teal/slate
      bg: isDark ? "hsl(180, 25%, 22%)" : "hsl(180, 35%, 38%)",
      color: "#fff",
      border: isDark ? "1px solid rgba(94, 234, 212, 0.2)" : "1px solid rgba(20, 184, 166, 0.4)",
    },
    primary: {
      bg: isDark ? theme.palette.primary.dark : theme.palette.primary.main,
      color: "#fff",
      border: isDark ? "1px solid rgba(0, 40, 67, 0.4)" : "1px solid rgba(0, 40, 67, 0.35)",
    },
    success: {
      bg: isDark ? theme.palette.success.dark : theme.palette.success.main,
      color: "#fff",
      border: isDark ? "1px solid rgba(160, 196, 109, 0.25)" : "1px solid rgba(122, 156, 74, 0.4)",
    },
    oyster: {
      // Oyster: no fill, no border, larger icon; text matches theme
      bg: "transparent",
      color: theme.palette.text.primary,
      border: "none",
    },
    oyster2: {
      // Oyster2: same treatment as Oyster
      bg: "transparent",
      color: theme.palette.text.primary,
      border: "none",
    },
  };
  const pillStyles = colorMap[config.colorKey] || colorMap.default;
  const greenColor = theme.palette.success?.main || "#A0C46D";

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100%",
        minHeight: "100%",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        component="span"
        onClick={showCustomImage && customImageSrc ? () => setFullImageOpen(true) : undefined}
        role={showCustomImage && customImageSrc ? "button" : undefined}
        tabIndex={showCustomImage && customImageSrc ? 0 : undefined}
        onKeyDown={
          showCustomImage && customImageSrc
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setFullImageOpen(true);
                }
              }
            : undefined
        }
        sx={{
          display: "inline-flex",
          alignItems: "center",
          alignSelf: "center",
          justifyContent: "center",
          ...((config.tagLogo || config.tekktelicLogo) && { width: "100%" }),
          ...(config.oysterLogo && { width: 112, minWidth: 112, marginLeft: "20px" }),
          ...(config.tagLogo && { marginLeft: "-22px" }),
          ...(config.nfcPill && { marginLeft: "-8px" }),
          gap: config.oysterLogo ? "4px" : "3px",
          paddingLeft: "8px",
          paddingRight: "8px",
          paddingTop: config.oysterLogo ? "2.5px" : "2px",
          paddingBottom: config.oysterLogo ? "2px" : "2px",
          borderRadius: "9999px",
          fontSize: "0.6875rem",
          fontWeight: 600,
          letterSpacing: "0.02em",
          lineHeight: 1.25,
          whiteSpace: "nowrap",
          transition: "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
          backgroundColor: pillStyles.bg,
          color: pillStyles.color,
          border: pillStyles.border,
          ...((showCustomImage && customImageSrc) && { cursor: "pointer" }),
          "&:focus-visible": {
            outline: "none",
            boxShadow: `0 0 0 2px ${greenColor}40`,
          },
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
          },
          "& .MuiSvgIcon-root": {
            fontSize: "0.875rem",
            opacity: 1,
            margin: 0,
            flexShrink: 0,
            color: "inherit",
          },
          "& img": {
            width: config.tagLogo ? "40px" : config.tekktelicLogo ? "37px" : config.oysterLogo ? "36px" : "20px",
            height: config.tagLogo ? "40px" : config.tekktelicLogo ? "37px" : config.oysterLogo ? "36px" : "20px",
            minWidth: config.tagLogo ? "40px" : config.tekktelicLogo ? "37px" : config.oysterLogo ? "36px" : "20px",
            minHeight: config.tagLogo ? "40px" : config.tekktelicLogo ? "37px" : config.oysterLogo ? "36px" : "20px",
            objectFit: "contain",
            flexShrink: 0,
            opacity: 1,
            display: "block",
            // Sharper rendering when image is scaled (reduces blur at 100% zoom)
            ...((config.ribbon || config.tagLogo) && { imageRendering: "crisp-edges" }),
            ...(config.oysterLogo && { alignSelf: "center" }),
            ...(config.ribbon && {
              // Higher contrast sharpens text in the logo; drop-shadows add edge definition for readability
              filter: isDark
                ? "brightness(1.4) contrast(1.5) saturate(1.3) drop-shadow(0 0 0.5px rgba(255,255,255,0.4)) drop-shadow(0 1px 2px rgba(0,0,0,0.35))"
                : "brightness(1.45) contrast(1.45) saturate(1.25) drop-shadow(0 0 0.5px rgba(0,0,0,0.25)) drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
            }),
            ...(config.oysterLogo && {
              filter: isDark ? "brightness(1.25) contrast(1.1)" : "brightness(1.7) contrast(1.15)",
            }),
          },
        }}
      >
        {showCustomImage ? (
          config.oysterLogo ? (
            <Box
              component="span"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: "4px",
                width: "100%",
                flexShrink: 0,
              }}
            >
              <Box
                component="span"
                sx={{
                  position: "relative",
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={customImageSrc}
                  alt=""
                  aria-hidden
                  onError={() => notifyOysterFailed()}
                  style={{
                    display: "block",
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  height: "36px",
                }}
              >
                {label}
              </Box>
            </Box>
          ) : (
            <img
              src={customImageSrc}
              srcSet={config.logoImg2x ? `${config.logoImg2x} 2x` : undefined}
              alt=""
              aria-hidden
              onError={() => notifyRibbonFailed()}
            />
          )
        ) : Icon ? (
          <Icon sx={{ fontSize: "0.875rem", opacity: 1, color: "inherit" }} />
        ) : null}
        {!config.oysterLogo && label}
      </Box>

      {/* Full-size tracker image dialog */}
      <Dialog
        open={fullImageOpen}
        onClose={() => setFullImageOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            maxWidth: "90vw",
            maxHeight: "90vh",
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: 24,
          },
        }}
      >
        <Box sx={{ position: "relative", p: 0 }}>
          <IconButton
            aria-label="Close"
            onClick={() => setFullImageOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              zIndex: 1,
              color: "white",
              bgcolor: "rgba(0,0,0,0.5)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={customImageSrc}
            srcSet={config.logoImg2x ? `${config.logoImg2x} 2x` : undefined}
            alt={`${label} tracker`}
            style={{
              display: "block",
              maxWidth: "90vw",
              maxHeight: "90vh",
              width: "auto",
              height: "auto",
              objectFit: "contain",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              py: 1,
              px: 2,
              background: "linear-gradient(transparent, rgba(0,0,0,0.6))",
              color: "white",
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            {label}
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
